import { useState, useEffect, useCallback } from 'react';
import { customFieldsAPI } from '../../utils/api';
import { Plus, Pencil, Trash2, GripVertical, ChevronRight, AlertCircle } from 'lucide-react';
import { useToast, apiError } from '../../components/Toast';

const ENTITY_TYPES = [
  { value:'project',        label:'Projects',         icon:'📁', desc:'Fields shown on the Project create/edit form' },
  { value:'task',           label:'Tasks',            icon:'✅', desc:'Fields shown on the Task create/edit form' },
  { value:'activity',       label:'Activities',       icon:'🔄', desc:'Fields shown on the Activity form' },
  { value:'bug',            label:'Bug Reports',      icon:'🐛', desc:'Fields shown on the Bug Report form' },
  { value:'change_request', label:'Change Requests',  icon:'📋', desc:'Fields shown on the Change Request form' },
  { value:'client',         label:'Clients',          icon:'🏢', desc:'Fields shown on the Client form' },
  { value:'training',       label:'Training Sessions',icon:'🎓', desc:'Fields shown on the Training form' },
];

const FIELD_TYPES = [
  { value:'text',      label:'Short Text',    example:'e.g. Reference code, external ID' },
  { value:'textarea',  label:'Long Text',     example:'e.g. Additional notes, context' },
  { value:'number',    label:'Number',        example:'e.g. Priority score, version' },
  { value:'date',      label:'Date',          example:'e.g. Review date, deadline' },
  { value:'boolean',   label:'Yes / No',      example:'e.g. Is billable, requires sign-off' },
  { value:'select',    label:'Dropdown',      example:'e.g. Region, department, category' },
  { value:'multiselect',label:'Multi-select', example:'e.g. Tags, stakeholders, skills' },
  { value:'url',       label:'URL / Link',    example:'e.g. External ticket, document link' },
  { value:'email',     label:'Email',         example:'e.g. Contact email, CC address' },
];

const EMPTY_FORM = {
  field_key: '', label: '', field_type: 'text',
  is_required: false, sort_order: 0, options: '',
};

export default function AdminMetadataPage() {
  const toast = useToast();
  const [selectedEntity, setSelectedEntity] = useState('project');
  const [fields,         setFields]         = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState('');
  const [showForm,       setShowForm]       = useState(false);
  const [editField,      setEditField]      = useState(null);
  const [form,           setForm]           = useState({ ...EMPTY_FORM });
  const [formError,      setFormError]      = useState('');
  const [saving,         setSaving]         = useState(false);
  const [deleteTarget,   setDeleteTarget]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await customFieldsAPI.getAll(selectedEntity);
      setFields(r.data || []);
    } catch(e) {
      setError(e.response?.data?.message || 'Failed to load custom fields');
    } finally { setLoading(false); }
  }, [selectedEntity]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditField(null);
    setForm({ ...EMPTY_FORM, sort_order: fields.length });
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (field) => {
    setEditField(field);
    setForm({
      field_key:   field.field_key,
      label:       field.label,
      field_type:  field.field_type,
      is_required: field.is_required,
      sort_order:  field.sort_order,
      options:     Array.isArray(field.options) ? field.options.join(', ') : (field.options || ''),
    });
    setFormError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.label.trim()) return setFormError('Field label is required');
    if (!form.field_key.trim()) return setFormError('Field key is required');
    if (['select','multiselect'].includes(form.field_type) && !form.options.trim())
      return setFormError('Options are required for dropdown/multi-select fields');

    setSaving(true); setFormError('');
    const payload = {
      applies_to:  selectedEntity,
      field_key:   form.field_key.toLowerCase().replace(/\s+/g,'_').replace(/[^a-z0-9_]/g,''),
      label:       form.label.trim(),
      field_type:  form.field_type,
      is_required: form.is_required,
      sort_order:  parseInt(form.sort_order) || 0,
      options:     ['select','multiselect'].includes(form.field_type)
        ? form.options.split(',').map(o => o.trim()).filter(Boolean)
        : [],
    };

    try {
      editField
        ? await customFieldsAPI.update(editField.id, payload)
        : await customFieldsAPI.create(payload);
      toast.success(editField ? 'Custom field updated' : 'Custom field created');
      setShowForm(false);
      load();
    } catch(e) {
      setFormError(e.response?.data?.message || 'Failed to save field');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await customFieldsAPI.delete(deleteTarget.id);
      toast.success('Custom field deleted');
      setDeleteTarget(null);
      load();
    } catch(e) {
      toast.error(apiError(e, 'Failed to delete field'));
    }
  };

  const currentEntity = ENTITY_TYPES.find(e => e.value === selectedEntity);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="page-title">Metadata Management</h1>
          <p className="text-sm mt-0.5" style={{ color:'var(--text-secondary)' }}>
            Define custom fields for each entity type. These fields appear on the corresponding forms throughout the application.
          </p>
        </div>
        <button onClick={openCreate} className="btn-primary flex items-center gap-1.5 text-sm">
          <Plus size={14}/> Add Custom Field
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-xl text-sm flex items-start gap-2" style={{ background:'rgba(239,68,68,.08)', color:'#ef4444', border:'1px solid rgba(239,68,68,.2)' }}>
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5"/>
          <span>{error}</span>
        </div>
      )}

      {/* Info banner */}
      <div className="card-alt text-sm space-y-1">
        <p className="font-semibold" style={{ color:'var(--text)' }}>How custom fields work</p>
        <p style={{ color:'var(--text-secondary)' }}>
          Custom fields are additional data points you can collect on any entity. Once defined here, they appear automatically on the corresponding create and edit forms. Field values are stored per-record and can be used for reporting.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Entity selector */}
        <div className="card overflow-hidden p-0">
          <div className="px-4 py-3 border-b text-xs font-semibold uppercase tracking-wider"
            style={{ borderColor:'var(--border)', background:'var(--surface-alt)', color:'var(--text-secondary)' }}>
            Entity Type
          </div>
          <div className="divide-y" style={{ borderColor:'var(--border)' }}>
            {ENTITY_TYPES.map(entity => (
              <button key={entity.value}
                onClick={() => { setSelectedEntity(entity.value); setShowForm(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-[var(--surface-alt)]"
                style={{ background: selectedEntity === entity.value ? 'rgba(46,83,159,.08)' : 'transparent' }}>
                <span className="text-lg flex-shrink-0">{entity.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium" style={{ color: selectedEntity === entity.value ? '#2E539F' : 'var(--text)' }}>
                    {entity.label}
                  </p>
                </div>
                {selectedEntity === entity.value && <ChevronRight size={14} style={{ color:'#2E539F' }}/>}
              </button>
            ))}
          </div>
        </div>

        {/* Field list + form */}
        <div className="lg:col-span-3 space-y-4">
          {/* Entity info header */}
          <div className="card flex items-center gap-3 py-3">
            <span className="text-2xl">{currentEntity?.icon}</span>
            <div>
              <p className="font-semibold" style={{ color:'var(--text)' }}>{currentEntity?.label} Custom Fields</p>
              <p className="text-xs" style={{ color:'var(--text-secondary)' }}>{currentEntity?.desc}</p>
            </div>
            <span className="ml-auto text-sm font-medium" style={{ color:'#2E539F' }}>
              {fields.length} field{fields.length !== 1 ? 's' : ''} defined
            </span>
          </div>

          {/* Field list */}
          <div className="card overflow-hidden p-0">
            {loading ? (
              <div className="flex justify-center py-10"><div className="w-5 h-5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/></div>
            ) : fields.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-3xl mb-2">📋</p>
                <p className="font-semibold" style={{ color:'var(--text)' }}>No custom fields yet</p>
                <p className="text-sm mt-1" style={{ color:'var(--text-secondary)' }}>
                  Click "Add Custom Field" to define the first field for {currentEntity?.label}.
                </p>
                <button onClick={openCreate} className="btn-primary text-sm mt-4 flex items-center gap-1.5 mx-auto">
                  <Plus size={13}/> Add First Field
                </button>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background:'var(--surface-alt)', borderBottom:'2px solid var(--border)' }}>
                    {['Order','Field Label','Key','Type','Required','Actions'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {fields.sort((a,b) => a.sort_order - b.sort_order).map((field, i) => (
                    <tr key={field.id} className="border-b hover:bg-[var(--surface-alt)] transition-colors"
                      style={{ borderColor:'var(--border)', background: i%2===1 ? 'rgba(0,0,0,.015)' : 'transparent' }}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <GripVertical size={13} style={{ color:'var(--text-secondary)' }}/>
                          <span className="text-xs font-medium" style={{ color:'var(--text-secondary)' }}>{field.sort_order}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium" style={{ color:'var(--text)' }}>{field.label}</p>
                        {field.field_type === 'select' || field.field_type === 'multiselect' ? (
                          <p className="text-xs mt-0.5" style={{ color:'var(--text-secondary)' }}>
                            Options: {(Array.isArray(field.options) ? field.options : []).join(', ') || 'None set'}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        <code className="text-xs px-1.5 py-0.5 rounded" style={{ background:'var(--surface-alt)', color:'#2E539F' }}>
                          {field.field_key}
                        </code>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                          style={{ background:'rgba(46,83,159,.1)', color:'#2E539F' }}>
                          {FIELD_TYPES.find(t => t.value === field.field_type)?.label || field.field_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium ${field.is_required ? 'text-red-500' : 'text-emerald-500'}`}>
                          {field.is_required ? '✓ Required' : 'Optional'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEdit(field)} title="Edit field"
                            className="p-1.5 rounded-lg hover:bg-[var(--surface-alt)] transition-colors"
                            style={{ color:'var(--text-secondary)' }}>
                            <Pencil size={12}/>
                          </button>
                          <button onClick={() => setDeleteTarget(field)} title="Delete field"
                            className="p-1.5 rounded-lg hover:bg-red-50 transition-colors text-red-400">
                            <Trash2 size={12}/>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Add/Edit form */}
          {showForm && (
            <div className="card border-2" style={{ borderColor:'#2E539F' }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold" style={{ color:'var(--text)' }}>
                  {editField ? 'Edit Field' : 'New Custom Field'} — {currentEntity?.label}
                </h3>
                <button onClick={() => setShowForm(false)} className="text-xs px-2 py-1 rounded border"
                  style={{ borderColor:'var(--border)', color:'var(--text-secondary)' }}>Cancel</button>
              </div>

              {formError && (
                <div className="p-3 rounded-xl text-sm mb-4" style={{ background:'rgba(239,68,68,.08)', color:'#ef4444' }}>
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Field Label <span className="text-red-500">*</span></label>
                  <input className="input" value={form.label} placeholder="e.g. Contract Reference"
                    onChange={e => {
                      const label = e.target.value;
                      setForm(f => ({
                        ...f,
                        label,
                        field_key: editField ? f.field_key : label.toLowerCase().replace(/\s+/g,'_').replace(/[^a-z0-9_]/g,'')
                      }));
                    }}/>
                  <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>Displayed as the form field label</p>
                </div>

                <div>
                  <label className="label">Field Key <span className="text-red-500">*</span></label>
                  <input className="input font-mono text-sm" value={form.field_key} placeholder="e.g. contract_reference"
                    onChange={e => setForm(f => ({ ...f, field_key: e.target.value.toLowerCase().replace(/\s+/g,'_').replace(/[^a-z0-9_]/g,'') }))}
                    disabled={!!editField}/>
                  <p className="text-xs mt-1" style={{ color: editField ? '#f97316' : 'var(--text-secondary)' }}>
                    {editField ? 'Key cannot be changed after creation' : 'Auto-generated — unique identifier, letters/numbers/underscores only'}
                  </p>
                </div>

                <div>
                  <label className="label">Field Type <span className="text-red-500">*</span></label>
                  <select className="select" value={form.field_type} onChange={e => setForm(f => ({ ...f, field_type: e.target.value }))}>
                    {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label} — {t.example}</option>)}
                  </select>
                </div>

                <div>
                  <label className="label">Sort Order</label>
                  <input type="number" className="input w-28" value={form.sort_order} min={0}
                    onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value)||0 }))}/>
                  <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>Lower numbers appear first</p>
                </div>

                {['select','multiselect'].includes(form.field_type) && (
                  <div className="md:col-span-2">
                    <label className="label">Options <span className="text-red-500">*</span></label>
                    <input className="input" value={form.options} placeholder="Option 1, Option 2, Option 3"
                      onChange={e => setForm(f => ({ ...f, options: e.target.value }))}/>
                    <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>Comma-separated list of options</p>
                  </div>
                )}

                <div className="md:col-span-2">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <button type="button" onClick={() => setForm(f => ({ ...f, is_required: !f.is_required }))}
                      className="relative w-10 h-5 rounded-full transition-colors flex-shrink-0"
                      style={{ background: form.is_required ? '#2E539F' : 'var(--border)' }}>
                      <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all"
                        style={{ left: form.is_required ? 22 : 2 }}/>
                    </button>
                    <div>
                      <p className="text-sm font-medium" style={{ color:'var(--text)' }}>Required field</p>
                      <p className="text-xs" style={{ color:'var(--text-secondary)' }}>
                        Users must fill this field before saving the {currentEntity?.label.slice(0,-1).toLowerCase()}
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-5 pt-4 border-t" style={{ borderColor:'var(--border)' }}>
                <button onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
                <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2">
                  {saving && <span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
                  {editField ? 'Save Changes' : 'Create Field'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDeleteTarget(null)}/>
          <div className="relative rounded-xl shadow-2xl w-full max-w-sm p-6 z-10" style={{ background:'var(--surface)', border:'1px solid var(--border)' }}>
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5"/>
              <div>
                <h3 className="font-bold" style={{ color:'var(--text)' }}>Delete Custom Field</h3>
                <p className="text-sm mt-1" style={{ color:'var(--text-secondary)' }}>
                  Delete <strong>"{deleteTarget.label}"</strong> (<code className="text-xs">{deleteTarget.field_key}</code>)?
                </p>
                <p className="text-sm mt-2 text-red-500 font-medium">
                  Any data already stored in this field will be permanently lost.
                </p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteTarget(null)} className="btn-secondary">Cancel</button>
              <button onClick={handleDelete} className="btn-danger">Delete Field</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
